'use client'
import type { ComponentProps, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { SbBaseBlockProps } from '@/services/storyblok/storyblok'
import type { SbBlokData } from '@storyblok/react'
import { StoryblokComponent, storyblokEditable } from '@storyblok/react'
import { Button } from 'ui/src/components/Button/Button'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { FullscreenDialog } from 'ui/src/components/Dialog/FullscreenDialog'
import { LargeDialog } from 'ui/src/components/Dialog/LargeDialog'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { visuallyHidden } from 'ui/src/utils/visibility.css'

export type ModalBlockProps = SbBaseBlockProps<{
  buttonText: string
  buttonVariant?: ComponentProps<typeof Button>['variant']
  buttonSize?: ComponentProps<typeof Button>['size']
  modalContent: Array<SbBlokData>
  useLegacyFullscreenDialog?: boolean
}>

export const ModalBlock = ({ blok, nested }: ModalBlockProps) => {
  const { t } = useTranslation()
  let dialogContent: ReactNode
  if (blok.useLegacyFullscreenDialog || blok.useLegacyFullscreenDialog == null) {
    dialogContent = (
      <FullscreenDialog.Modal accessibleCloseLabel={t('DIALOG_CLOSE')}>
        <Dialog.Title className={visuallyHidden} />
        {blok.modalContent.map((nestedBlock) => (
          // FullscreenDialog.Modal render things at the top level, so we need to pass nested={false} here
          // so nested blocks include lateral padding
          <StoryblokComponent key={nestedBlock._uid} blok={nestedBlock} nested={false} />
        ))}
      </FullscreenDialog.Modal>
    )
  } else {
    dialogContent = (
      <LargeDialog.Content>
        <LargeDialog.CloseIconButton aria-label={t('DIALOG_CLOSE')} />
        <Dialog.Title className={visuallyHidden} />
        <LargeDialog.ScrollableInnerContent>
          {blok.modalContent.map((nestedBlock) => (
            <StoryblokComponent key={nestedBlock._uid} blok={nestedBlock} nested={true} />
          ))}
        </LargeDialog.ScrollableInnerContent>
      </LargeDialog.Content>
    )
  }

  const trigger = (
    <LargeDialog.Root>
      <Dialog.Trigger asChild>
        <Button
          {...storyblokEditable(blok)}
          variant={blok.buttonVariant ?? 'primary'}
          size={blok.buttonSize ?? 'medium'}
        >
          {blok.buttonText}
        </Button>
      </Dialog.Trigger>
      {dialogContent}
    </LargeDialog.Root>
  )

  // If the modal is nested, return the trigger so it can be used as a child of another block.
  if (nested) {
    return trigger
  }

  return <div className={sprinkles({ display: 'flex', justifyContent: 'center', paddingInline: 'md' })}>{trigger}</div>
}
